import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import ShoppingBasketInsuranceSums from './ShoppingBasketInsuranceSums';
import ShoppingBasketSpecialConditions from './ShoppingBasketSpecialConditions';
import { Button } from '@flos/react-ui';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { coverableDetailsAtom } from '../../../../recoil/atoms/coverableDetailsAtom';
import ImportantInformation from '../../../../shared_components/ImportantInformation/ImportantInformation';
import Coverages from '../../../../shared_components/Coverages/Coverages';
import {
    isAgricultureCoverable,
    isWocOrCtpCoverable,
    isPrivateContentLiabilityOnly
} from '../../../../utils/coverablesUtil';
import { coverableChangesAtom } from '../../../../recoil/atoms/coverableChangesAtom';
import ShoppingBasketCoverableChangesList from './ShoppingBasketCoverableChangesList/ShoppingBasketCoverableChangesList';
import { businessFlowSelector } from '../../../../recoil/selectors/getBusinessFlowSelector';
import { useHippoText } from '../../../../hooks/useHippoText';
import useWizardNavigation from '../../../../hooks/useWizardNavigation';
import { accountHolderAtom } from '../../../../recoil/atoms/accountHolderAtom';
import { useDocumentPreview } from '../../../../hooks/useDocumentPreview';

const ShoppingBasketExpanded = ({ coverable }: { coverable: CoverableDTO }): React.JSX.Element => {
    const { wizardNextStep } = useWizardNavigation();
    const getHippoText = useHippoText();
    const setCoverableDetails = useSetRecoilState(coverableDetailsAtom);
    const [accountHolderData] = useRecoilState(accountHolderAtom);
    const { isCVR } = accountHolderData;
    const coverableChanges = useRecoilValue(coverableChangesAtom);
    const changes = coverableChanges.filter(
        (cov) => cov.coverableId === coverable.fixedId && cov.linePrefix === coverable.lobCode
    );
    const mergedChanges =
        changes.length > 0
            ? changes.reduce(
                  (prev, current) => {
                      return { ...prev, changes: prev.changes.concat(current.changes) };
                  },
                  { ...changes[0], changes: [] }
              )
            : undefined;
    const isAgricultureLob = isAgricultureCoverable(coverable);
    const isPrcLiabilityOnly = isPrivateContentLiabilityOnly(coverable) ?? false;
    const businessFlow = useRecoilValue(businessFlowSelector);
    const isWocCtpLob = isWocOrCtpCoverable(coverable);
    const { generateQuoteDocument, isDocumentLoading } = useDocumentPreview();

    const quoteBtnTxtAgriCPRWoCCTP = getHippoText('DSS-COVERABLE-DETAILS-PAGE-AGRICULTURE-BUTTON');
    const quoteBtnTxtAgriCPR = getHippoText('DSS-COVERABLE-DETAILS-PAGE-AGRICULTURE-BUTTON');
    const quoteBtnTxtAgriCVR = getHippoText('DSS-COVERABLE-DETAILS-PAGE-AGRICULTURE-BUTTON-CVR');

    const setDetailsAndGotoNextStep = (): void => {
        setCoverableDetails(coverable);
        wizardNextStep();
    };

    return (
        <div className="u-flex-box u-flex-box--col u-gap-3 u-gap-5@md">
            {!isAgricultureLob ? (
                <>
                    {!mergedChanges && (
                        <>
                            <ImportantInformation
                                coverable={coverable}
                                shouldRenderShoppingBasketDesign
                            />
                            {businessFlow !== 'OPC' && (
                                <div className="u-flex-box u-flex-box--col u-gap-3 u-gap-5@md">
                                    <Coverages
                                        coverages={coverable.coverages}
                                        shouldRenderTitle
                                        flavourName={coverable.flavourName}
                                        coverableId={coverable.fixedId}
                                    />
                                    {!isPrcLiabilityOnly && (
                                        <ShoppingBasketInsuranceSums coverable={coverable} />
                                    )}
                                </div>
                            )}
                            <ShoppingBasketSpecialConditions coverable={coverable} />
                        </>
                    )}

                    {mergedChanges && (
                        <ShoppingBasketCoverableChangesList
                            coverableChanges={mergedChanges}
                            coverable={coverable}
                        />
                    )}
                    {businessFlow === 'DA' && !mergedChanges && (
                        <div className="u-flex-box u-flex-justify-center">
                            <Button theme="primary" onClick={setDetailsAndGotoNextStep}>
                                Se alle oplysninger
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <>
                    {isWocCtpLob && !isCVR && (
                        <>
                            <ImportantInformation
                                coverable={coverable}
                                shouldRenderShoppingBasketDesign
                            />
                            <Coverages
                                coverages={coverable.coverages}
                                shouldRenderTitle
                                flavourName={coverable.flavourName}
                            />
                            {!isPrcLiabilityOnly && (
                                <ShoppingBasketInsuranceSums coverable={coverable} />
                            )}
                            <ShoppingBasketSpecialConditions coverable={coverable} />
                        </>
                    )}
                    <div className="u-flex-box u-flex-justify-center">
                        <Button
                            className="u-flex-box"
                            theme="primary"
                            disabled={isDocumentLoading}
                            isLoading={isDocumentLoading}
                            loadingText={getHippoText('DSS-COMMON-BUTTON-WAIT')}
                            onClick={generateQuoteDocument}
                        >
                            {isWocCtpLob && !isCVR && quoteBtnTxtAgriCPRWoCCTP}
                            {!isWocCtpLob && !isCVR && quoteBtnTxtAgriCPR}
                            {isCVR && quoteBtnTxtAgriCVR}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ShoppingBasketExpanded;
